import { createStore } from 'vuex'
import axios from 'axios'
import { getApiUrl } from '@/config/api'

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result || '')
    reader.onerror = () => reject(reader.error || new Error('读取文件失败'))
    reader.readAsDataURL(file)
  })

export default createStore({
  state: {
    user: JSON.parse(localStorage.getItem('user')) || null,
    token: localStorage.getItem('token') || null,
    chatMessages: [],
    isLoading: false
  },
  getters: {
    isAuthenticated: state => !!state.token,
    currentUser: state => state.user,
    username: state => state.user ? state.user.username : '',
    avatar: state => state.user ? state.user.avatar : ''
  },
  mutations: {
    setUser(state, user) {
      state.user = user
      localStorage.setItem('user', JSON.stringify(user))
    },
    setToken(state, token) {
      state.token = token
      localStorage.setItem('token', token)
    },
    clearUserData(state) {
      state.user = null
      state.token = null
      localStorage.removeItem('user')
      localStorage.removeItem('token')
    },
    addChatMessage(state, message) {
      state.chatMessages.push(message)
    },
    clearChatMessages(state) {
      state.chatMessages = []
    },
    setLoading(state, status) {
      state.isLoading = status
    }
  },
  actions: {
    // 用户登录
    async login({ commit }, credentials) {
      const response = await axios.post('/api/users/login', credentials)
      const { user, token } = response.data
      commit('setUser', user)
      commit('setToken', token)
      return response
    },
    
    // 用户注册
    async register({ commit }, userData) {
      const response = await axios.post('/api/users/register', userData)
      const { user, token } = response.data
      commit('setUser', user)
      commit('setToken', token)
      return response
    },
    
    // 退出登录
    logout({ commit }) {
      commit('clearUserData')
      commit('clearChatMessages')
    },
    
    // 获取用户资料
    async getProfile({ commit, state }) {
      try {
        const response = await axios.get('/api/users/profile', {
          headers: {
            'Authorization': `Bearer ${state.token}`
          }
        })
        commit('setUser', response.data.user)
        return response.data.user
      } catch (error) {
        console.error('获取用户资料失败:', error)
        throw error
      }
    },
    
    // 更新用户资料
    async updateUserProfile({ commit, state }, userData) {
      try {
        const response = await axios.put('/api/users/profile', userData, {
          headers: {
            'Authorization': `Bearer ${state.token}`,
            'Content-Type': 'application/json'
          }
        })
        
        // 更新本地用户信息
        const updatedUser = response.data.user
        commit('setUser', updatedUser)
        
        return response.data
      } catch (error) {
        console.error('更新用户资料失败:', error)
        throw error
      }
    },
    
    // 删除所有对话
    async deleteAllChats({ commit }) {
      try {
        // 模拟API延迟
        await new Promise(resolve => setTimeout(resolve, 800))
        
        // 清空聊天消息
        commit('clearChatMessages')
        
        return { success: true }
      } catch (error) {
        console.error('删除所有对话失败:', error)
        throw error
      }
    },
    
    // 删除账号
    async deleteAccount({ dispatch }) {
      try {
        // 模拟API延迟
        await new Promise(resolve => setTimeout(resolve, 800))
        
        // 注销用户
        await dispatch('logout')
        
        return { success: true }
      } catch (error) {
        console.error('删除账号失败:', error)
        throw error
      }
    },
    
    // 发送聊天消息
    async sendMessage({ commit, state }, message) {
      try {
        commit('setLoading', true)
        
        // 添加到前端界面 - 用户消息
        let stableImageUrl = ''
        let stableVideoUrl = ''
        if (message.image) {
          try {
            stableImageUrl = await readFileAsDataUrl(message.image)
          } catch (e) {
            console.warn('图片转 data URL 失败，回退到 blob URL:', e)
          }
        }
        if (message.video) {
          try {
            stableVideoUrl = await readFileAsDataUrl(message.video)
          } catch (e) {
            console.warn('视频转 data URL 失败，回退到 blob URL:', e)
          }
        }
        const localImageUrl =
          stableImageUrl ||
          (typeof message.localImageUrl === 'string' && message.localImageUrl
            ? message.localImageUrl
            : (message.image ? URL.createObjectURL(message.image) : ''))
        const localVideoUrl =
          stableVideoUrl ||
          (typeof message.localVideoUrl === 'string' && message.localVideoUrl
            ? message.localVideoUrl
            : (message.video ? URL.createObjectURL(message.video) : ''))
        const userMessage = {
          content: message.text || '',
          images: localImageUrl ? [localImageUrl] : [],
          videos: localVideoUrl ? [localVideoUrl] : [],
          sender: 'user',
          timestamp: new Date().toISOString()
        }
        commit('addChatMessage', userMessage)
        
        let response;
        const uploadFileToStorage = async (file) => {
          const authHeader = state.token ? `Bearer ${state.token}` : ''
          try {
            const signRes = await axios.post(
              getApiUrl('/api/videos/storage/presign'),
              { filename: file.name, scene: 'chat' },
              {
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': authHeader,
                  'ngrok-skip-browser-warning': 'any'
                }
              }
            )
            const uploadUrl = signRes?.data?.data?.upload_url
            const fileUrl = signRes?.data?.data?.file_url
            if (!uploadUrl || !fileUrl) {
              throw new Error('获取存储上传签名失败')
            }
            await axios.put(uploadUrl, file, {
              headers: {
                'Content-Type': file.type || 'application/octet-stream'
              }
            })
            return fileUrl
          } catch (err) {
            const fallbackData = new FormData()
            fallbackData.append('file', file)
            fallbackData.append('scene', 'chat')
            const fallbackRes = await axios.post(
              getApiUrl('/api/videos/storage/upload'),
              fallbackData,
              {
                headers: {
                  'Content-Type': 'multipart/form-data',
                  'Authorization': authHeader,
                  'ngrok-skip-browser-warning': 'any'
                }
              }
            )
            const fileUrl = fallbackRes?.data?.data?.file_url
            if (!fileUrl) {
              throw err
            }
            return fileUrl
          }
        }
        
        // 根据消息类型选择不同的API端点
        if (message.image) {
          // 处理图像消息
          const imageUrl = await uploadFileToStorage(message.image)
          const formData = new FormData()
          formData.append('text', message.text || '')
          formData.append('image_url', imageUrl)
          if (message.conversation_id) {
            formData.append('conversation_id', message.conversation_id)
          }
          
          response = await axios.post('/api/chat/image', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': state.token ? `Bearer ${state.token}` : '',
              'ngrok-skip-browser-warning': 'any'
            }
          })
        } else if (message.video) {
          // 处理视频消息
          const videoUrl = await uploadFileToStorage(message.video)
          const formData = new FormData()
          formData.append('text', message.text || '')
          formData.append('video_url', videoUrl)
          if (message.conversation_id) {
            formData.append('conversation_id', message.conversation_id)
          }
          if (message.video_id) {
            formData.append('video_id', message.video_id)
          }
          
          response = await axios.post('/api/chat/video', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': state.token ? `Bearer ${state.token}` : '',
              'ngrok-skip-browser-warning': 'any'
            }
          })
        } else {
          // 处理纯文本消息
          // 构建消息历史
          const messages = [
            ...state.chatMessages.map(msg => ({
              role: msg.sender === 'user' ? 'user' : 'assistant',
              content: msg.content
            })),
            { role: 'user', content: message.text }
          ];
          
          const requestData = { messages }
          // 如果有conversation_id，添加到请求中
          if (message.conversation_id) {
            requestData.conversation_id = message.conversation_id
          }
          
          response = await axios.post('/api/chat', 
            requestData,
            {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': state.token ? `Bearer ${state.token}` : '',
                'ngrok-skip-browser-warning': 'any'
              }
            }
          )
        }
        
        // 添加模型回复到前端界面
        const botMessage = {
          content: response.data.content || "对不起，我无法理解您的问题。",
          images: [],
          videos: [],
          sender: 'bot',
          timestamp: new Date().toISOString()
        }
        commit('addChatMessage', botMessage)
        commit('setLoading', false)
        
        // 返回bot消息和conversation_id
        return {
          ...botMessage,
          conversation_id: response.data.conversation_id
        }
      } catch (error) {
        console.error('发送消息出错:', error)
        
        // 添加错误消息
        const errorMessage = {
          content: "抱歉，发生了错误: " + (error.response?.data?.error || error.message || "未知错误"),
          sender: 'bot',
          timestamp: new Date().toISOString()
        }
        commit('addChatMessage', errorMessage)
        commit('setLoading', false)
        throw error
      }
    }
  }
}) 